import { createVoiceAgent } from "@microsoft/voice-widget";
import { createWidgetShell } from "./createWidgetShell.js";
import type { MountVoiceAgentOptions, VoiceAgentController } from "./types.js";

/**
 * Thin adapter: wires the headless {@link createVoiceAgent} core to the Preact shell.
 * The core owns session state; the shell reflects it and emits intents. The core runs
 * no requestAnimationFrame — this adapter owns the audio-level loop, pulling
 * core.getOutputLevel() and pushing it to the shell while connected.
 */
export function mountVoiceAgent(
  target: Element,
  opts: MountVoiceAgentOptions,
): VoiceAgentController {
  const { ui, onExpand, onCollapse, onTranscript, ...sessionOpts } = opts;
  const core = createVoiceAgent({
    ...sessionOpts,
    onTranscript: (message) => {
      shell.appendTranscript(message);
      onTranscript?.(message);
    },
  });

  function start(): Promise<void> {
    const status = core.getState().status;
    if (status !== "connecting" && status !== "connected") shell.clearTranscript();
    return core.start();
  }

  const shell = createWidgetShell(target, {
    ...ui,
    onExpand,
    onCollapse,
    onRequestStart: () => void start(),
    onRequestStop: () => void core.stop().catch(() => {}),
    onToggleMute: (muted) => core.setMuted(muted), // core is the single source of truth
    onSendText: (text) => core.sendText(text),
  });

  // Adapter-owned output-level loop. Runs while connected; writes the agent's output
  // level (0..1) into the shell's --va-level via setOutputLevel. Kept off the Preact
  // render path. Both fns idempotent so connected->connected (mode
  // changes) is a no-op — no cancel/restart churn.
  const raf =
    typeof globalThis.requestAnimationFrame === "function"
      ? globalThis.requestAnimationFrame.bind(globalThis)
      : undefined;
  let rafId: number | undefined;
  function startLevelLoop(): void {
    if (!raf || rafId !== undefined) return;
    const tick = (): void => {
      shell.setOutputLevel(core.getOutputLevel());
      rafId = raf(tick);
    };
    rafId = raf(tick);
  }
  function stopLevelLoop(): void {
    if (rafId === undefined) return;
    globalThis.cancelAnimationFrame?.(rafId);
    rafId = undefined;
    shell.setOutputLevel(0); // rest the halo
  }

  // Mirror core session state into the shell + drive the level loop.
  const unsubscribe = core.subscribe(() => {
    const s = core.getState();
    shell.setStatus(s.status);
    shell.setMode(s.mode);
    shell.setMuted(s.muted);
    if (s.status === "connected") startLevelLoop();
    else stopLevelLoop();
  });

  return {
    start() {
      return start();
    },
    stop() {
      return core.stop();
    },
    setMuted(muted: boolean) {
      core.setMuted(muted);
    },
    registerClientTool(name, handler) {
      return core.registerClientTool(name, handler);
    },
    sendText(text: string) {
      return core.sendText(text);
    },
    setLangStrings(langStrings) {
      shell.setLangStrings(langStrings);
    },
    setUiLocale(locale) {
      shell.setUiLocale(locale);
    },
    setTheme(theme) {
      shell.setTheme(theme);
    },
    setAccentColor(accentColor) {
      shell.setAccentColor(accentColor);
    },
    setOrbPalette(palette) {
      shell.setOrbPalette(palette);
    },
    getState() {
      return shell.getState(); // UI shape (WidgetViewState) — back-compat
    },
    destroy() {
      unsubscribe();
      stopLevelLoop();
      core.destroy();
      shell.destroy();
    },
  };
}
